import React, { useEffect, useState } from "react";
import {
  Card, CardContent, Typography, Grid, Box, Paper, Alert, AlertTitle,
  Accordion, AccordionSummary, AccordionDetails, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Switch, Chip, Stack,
  Pagination, IconButton, Tooltip, Badge, styled, alpha, Avatar
} from "@mui/material";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, XAxis, YAxis,
  Tooltip as RechartsTooltip, ResponsiveContainer, Cell, Legend,
  CartesianGrid, Area, AreaChart
} from "recharts";
import {
  AlertCircle, TrendingUp, Package, CircleDollarSign, ShoppingBag,
  ChevronDown, PoundSterling, ClipboardX, Warning, CheckCircle,
  ArrowUpRight, ArrowDownRight, Eye, EyeOff, Store, Users, Layers
} from "lucide-react";
import { collection, query, onSnapshot, getDocs, doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase-config";
import fetchRestaurantPerformance from "./fetchRestaurantPerformance";
import TodayInvoicesGrid from "../components/TodayInvoicesGrid";
import SalesCards from "../components/SalesCards";
import appTheme, { chefTheme } from "../theme";
import { useAuth } from "../auth-context";

// **STYLED COMPONENTS** (keeping your existing styles)
const DashboardContainer = styled(Box)({
  padding: appTheme.spacing.lg,
  backgroundColor: appTheme.colors.surfaceLuxury,
  minHeight: '100vh',
});

const StyledCard = styled(Card)({
  borderRadius: appTheme.borderRadius.rounded,
  boxShadow: appTheme.shadows.medium,
  border: `1px solid ${appTheme.colors.borderSubtle}`,
  transition: appTheme.transitions.luxury,
  backgroundColor: appTheme.colors.surface,
  overflow: 'hidden',
  "&:hover": {
    boxShadow: appTheme.shadows.strong,
    transform: "translateY(-4px)",
  }
});

const KPICard = styled(StyledCard)({
  background: appTheme.gradients.cardLuxury,
  position: 'relative',
  "&::before": {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: appTheme.gradients.hero,
  }
});

const ChartCard = styled(StyledCard)({
  '& .recharts-wrapper': {
    borderRadius: appTheme.borderRadius.medium,
  },
  '& .recharts-cartesian-grid-horizontal line': {
    stroke: alpha(appTheme.colors.borderPrimary, 0.3),
  },
  '& .recharts-cartesian-grid-vertical line': {
    stroke: alpha(appTheme.colors.borderPrimary, 0.3),
  }
});

const HeaderCard = styled(StyledCard)({
  background: appTheme.gradients.hero,
  color: appTheme.colors.champagne,
  marginBottom: appTheme.spacing.xl,
});

const Dashboard = () => {
  const { userRole } = useAuth();
  // **STATE MANAGEMENT**
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [restaurantPerformance, setRestaurantPerformance] = useState([]);
  const [salesTrend, setSalesTrend] = useState([]);
  const [todayOrders, setTodayOrders] = useState({});
  const [totalAvailable, setTotalAvailable] = useState({});
  const [totalSold, setTotalSold] = useState({});
  const [totalCombined, setTotalCombined] = useState({});
  const [todayRestaurantOrders, setTodayRestaurantOrders] = useState([]);
  const [restaurantUnpaidRevenue, setRestaurantUnpaidRevenue] = useState([]);
  const [isCutoffEnabled, setIsCutoffEnabled] = useState(false);
  const [pendingPurchaseOrders, setPendingPurchaseOrders] = useState([]);
  const [inventoryStatus, setInventoryStatus] = useState({ ingredients: 0, products: 0, derived: 0 });
  const [todayInvoices, setTodayInvoices] = useState([]);
  const [inventoryTypeData, setInventoryTypeData] = useState([]);
  const [lowStockPage, setLowStockPage] = useState(1);
  const [showAllLowStock, setShowAllLowStock] = useState(false);
  const [chartHeight, setChartHeight] = useState(300);

  const isChef = userRole === 'chef';

  const showFullDashboard = userRole === 'admin' || userRole === 'superadmin';
  const showLimitedDashboard = isChef;
  const showChefComponents = isChef;
  const activeTheme = isChef ? chefTheme : appTheme;

  const ITEMS_PER_PAGE = 6;

  // **CHART COLORS**
  const CHART_COLORS = [
    appTheme.colors.primary,
    appTheme.colors.secondary,
    appTheme.colors.accent,
    appTheme.colors.goldenHour,
    appTheme.colors.jade,
    appTheme.colors.warmCopper,
    '#6366F1',
    '#EC4899',
    '#8B5CF6',
    '#10B981'
  ];

  // **SAFE HELPERS**
  const safeNumber = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 0;
    return Number(value);
  };

  const safeToFixed = (value, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '0.00';
    return Number(value).toFixed(decimals);
  };

  // **CUSTOM TOOLTIP**
  const CustomTooltip = ({ active, payload, label, currency = true }) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{
          backgroundColor: appTheme.colors.surface,
          padding: appTheme.spacing.sm,
          borderRadius: appTheme.borderRadius.medium,
          boxShadow: appTheme.shadows.strong,
          border: `1px solid ${appTheme.colors.borderPrimary}`,
          minWidth: 200
        }}>
          <Typography variant="subtitle2" sx={{
            color: appTheme.colors.primary,
            fontWeight: appTheme.typography.fontWeights.bold,
            // mb: 1
            fontSize: '18px'
          }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: entry.color, fontWeight: 'bold', fontSize: '17px' }}>
                {entry.name}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '18px' }}>
                {currency ? `£${safeToFixed(entry.value)}` : entry.value}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  // **YOUR EXISTING FUNCTIONS**
  const getChartHeight = () => window.innerWidth <= 768 ? 250 : 320;

  const checkAndUpdateCutoffStatus = async () => {
    try {
      const docRef = doc(db, "cutoffTime", "GCMLKWYcSCWKq0RvKrbB");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setIsCutoffEnabled(docSnap.data().isCutoffEnabled);
      }
    } catch (error) {
      console.error("Error fetching cutoff status:", error);
    }
  };

  const handleToggleChange = async () => {
    const newValue = !isCutoffEnabled;
    setIsCutoffEnabled(newValue);
    try {
      const docRef = doc(db, "cutoffTime", "GCMLKWYcSCWKq0RvKrbB");
      await updateDoc(docRef, { isCutoffEnabled: newValue });
    } catch (error) {
      console.error("Error updating cutoff status:", error);
    }
  };

  const getResturantPerformanceData = async () => {
    const restaurantPerformanceData = await fetchRestaurantPerformance(db);
    setRestaurantPerformance(restaurantPerformanceData);
  };

  // **ADD THESE HELPER FUNCTIONS AFTER THE IMPORTS AND BEFORE THE COMPONENT**

  // UK Time helper functions
  const getUKTime = (date = new Date()) => {
    return new Date(date.toLocaleString("en-US", { timeZone: "Europe/London" }));
  };

  const formatUKTime = (timestamp) => {
    if (!timestamp) return 'N/A';

    let date;
    if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    return getUKTime(date).toLocaleString("en-GB", {
      timeZone: "Europe/London",
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTodayRangeText = () => {
    const ukNow = getUKTime();
    const todayStart = new Date(ukNow);
    todayStart.setHours(9, 0, 0, 0);

    if (ukNow.getHours() < 9) {
      todayStart.setDate(todayStart.getDate() - 1);
    }

    const todayEnd = new Date(todayStart);
    todayEnd.setDate(todayEnd.getDate() + 1);
    todayEnd.setHours(9, 0, 0, 0);

    return {
      start: todayStart.toLocaleDateString("en-GB", {
        timeZone: "Europe/London",
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      }),
      end: todayEnd.toLocaleDateString("en-GB", {
        timeZone: "Europe/London",
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  useEffect(() => {
    checkAndUpdateCutoffStatus();
    const handleResize = () => setChartHeight(getChartHeight());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // **MAIN DATA EFFECT WITH FIXES**
  useEffect(() => {
    const now = new Date();

    // **SEPARATE DATE RANGES FOR TODAY VS PERFORMANCE - 9AM TO 9AM UK TIME**
    const ukNow = new Date(now.toLocaleString("en-US", { timeZone: "Europe/London" }));

    const todayStart = new Date(ukNow);
    const todayEnd = new Date(ukNow);

    if (ukNow.getHours() < 9) {
      // Before 9AM UK time: today = yesterday 9AM to today 9AM
      todayStart.setDate(ukNow.getDate() - 1);
      todayStart.setHours(9, 0, 0, 0);
      todayEnd.setHours(9, 0, 0, 0);
    } else {
      // After 9AM UK time: today = today 9AM to tomorrow 9AM
      todayStart.setHours(9, 0, 0, 0);
      todayEnd.setDate(ukNow.getDate() + 1);
      todayEnd.setHours(9, 0, 0, 0);
    }

    console.log("🕐 DASHBOARD USEEFFECT INITIALIZED - 9AM-9AM UK TIME");
    console.log("📍 Current UK Time:", ukNow.toString());
    console.log("📅 TODAY's Range (9AM-9AM):", {
      start: todayStart.toISOString(),
      end: todayEnd.toISOString(),
      startUK: todayStart.toLocaleString("en-GB", { timeZone: "Europe/London" }),
      endUK: todayEnd.toLocaleString("en-GB", { timeZone: "Europe/London" })
    });

    // For performance charts, use broader range
    const performanceStart = new Date(now);
    performanceStart.setDate(now.getDate() - 30);
    performanceStart.setHours(0, 0, 0, 0);

    const performanceEnd = new Date(now);
    performanceEnd.setHours(23, 59, 59, 999);

    console.log("🕐 DASHBOARD USEEFFECT INITIALIZED");
    console.log("📅 TODAY's Range:", {
      todayStart: todayStart.toISOString(),
      todayEnd: todayEnd.toISOString()
    });
    console.log("📊 Performance Range:", {
      performanceStart: performanceStart.toISOString(),
      performanceEnd: performanceEnd.toISOString()
    });

    const invoicesRef = collection(db, "invoices");
    const purchaseOrdersRef = collection(db, "purchaseOrders");
    const ingredientsRef = collection(db, "ingredients");
    const inventoryRef = collection(db, "inventoryItems");

    // **ENHANCED INVENTORY LISTENER**
    const unsubscribeTotalAvailable = onSnapshot(inventoryRef, async (snapshot) => {
      console.log("\n📦 ===== INVENTORY LISTENER TRIGGERED =====");
      console.log("📦 Inventory snapshot size:", snapshot.size);

      let totalAvailableItems = 0;
      let totalAvailableCost = 0;
      let totalAvailableQuantity = 0;
      const lowStock = [];
      const typeCounts = { ingredient: 0, product: 0, derived: 0 };

      snapshot.forEach((doc) => {
        const item = doc.data();
        totalAvailableItems += 1;
        const itemCost = parseFloat(item.price) * safeNumber(item.availableQuantity) || 0;
        totalAvailableCost += itemCost;
        totalAvailableQuantity += safeNumber(item.availableQuantity) || 0;

        if (item.itemType) {
          typeCounts[item.itemType] = (typeCounts[item.itemType] || 0) + 1;
        }

        // Low stock analysis
        const itemLowStockThreshold = safeNumber(item.lowStockThreshold) || 5;
        const availableQty = safeNumber(item.availableQuantity);

        if (availableQty <= itemLowStockThreshold) {
          lowStock.push({
            id: doc.id,
            title: item.title,
            quantity: availableQty,
            threshold: itemLowStockThreshold,
            type: item.itemType || "unknown",
            units: item.units || "units",
            percentage: itemLowStockThreshold > 0 ? (availableQty / itemLowStockThreshold) * 100 : 0,
            severity: availableQty === 0 ? 'critical' : availableQty <= itemLowStockThreshold * 0.5 ? 'high' : 'medium'
          });
        }
      });

      // **PROCESS INGREDIENTS**
      try {
        const ingredientsSnapshot = await getDocs(ingredientsRef);
        const ingredientsCount = ingredientsSnapshot.size;
        typeCounts.ingredient += ingredientsCount;
        totalAvailableItems += ingredientsCount;

        ingredientsSnapshot.forEach((doc) => {
          const ingredient = doc.data();
          const ingredientLowStockThreshold = safeNumber(ingredient.lowStockThreshold) || 5;
          const availableQty = safeNumber(ingredient.availableQuantity);

          if (availableQty <= ingredientLowStockThreshold) {
            lowStock.push({
              id: doc.id,
              title: ingredient.title,
              quantity: availableQty,
              threshold: ingredientLowStockThreshold,
              type: "ingredient",
              units: ingredient.units || "units",
              percentage: ingredientLowStockThreshold > 0 ? (availableQty / ingredientLowStockThreshold) * 100 : 0,
              severity: availableQty === 0 ? 'critical' : availableQty <= ingredientLowStockThreshold * 0.5 ? 'high' : 'medium'
            });
          }
        });
      } catch (error) {
        console.error("❌ Error fetching ingredients:", error);
      }

      // **INVENTORY TYPE DATA**
      const inventoryTypeChartData = Object.entries(typeCounts).map(([type, count]) => ({
        type: type.charAt(0).toUpperCase() + type.slice(1),
        count,
        percentage: totalAvailableItems > 0 ? ((count / totalAvailableItems) * 100).toFixed(1) : 0
      })).filter(item => item.count > 0);

      // Sort low stock
      lowStock.sort((a, b) => {
        const severityOrder = { critical: 0, high: 1, medium: 2 };
        if (severityOrder[a.severity] !== severityOrder[b.severity]) {
          return severityOrder[a.severity] - severityOrder[b.severity];
        }
        return a.percentage - b.percentage;
      });

      // **CATEGORY DATA PROCESSING**
      console.log("\n🏷️ Processing category data...");
      try {
        const categoryRef = collection(db, "inventoryCategory");
        const categorySnapshot = await getDocs(categoryRef);
        console.log("🏷️ Categories snapshot size:", categorySnapshot.size);

        const categories = categorySnapshot.docs.map((doc) => {
          const categoryData = doc.data();
          console.log(`🏷️ Category:`, { id: doc.id, category: categoryData.category });
          return {
            id: doc.id,
            name: categoryData.category,
          };
        });

        const categoryData = categories.map((category) => {
          const salesItems = snapshot.docs.filter((itemDoc) => itemDoc.data().categoryId === category.id);
          console.log(`🏷️ Items in category ${category.name}:`, salesItems.length);

          const Sales = salesItems.reduce((total, itemDoc) => {
            const item = itemDoc.data();
            const qty = item.availableQuantity || 0;
            console.log(`🏷️ Item ${item.title} available quantity:`, qty);
            return total + qty;
          }, 0);

          console.log(`🏷️ Category ${category.name} total available:`, Sales);

          return {
            name: category.name.length > 12 ? category.name.substring(0, 12) + '...' : category.name,
            fullName: category.name,
            Sales
          };
        }).filter(cat => {
          const hasData = cat.Sales > 0;
          console.log(`🏷️ Category ${cat.fullName} included:`, hasData);
          return hasData;
        });

        console.log("🏷️ Final Category Data:", categoryData);
        setCategoryData(categoryData);
      } catch (error) {
        console.error("❌ Error processing categories:", error);
      }

      // **SET INVENTORY STATE**
      setTotalAvailable({
        items: totalAvailableItems,
        cost: totalAvailableCost.toFixed(2),
        quantity: totalAvailableQuantity,
      });
      setLowStockItems(lowStock);
      setInventoryStatus(typeCounts);
      setInventoryTypeData(inventoryTypeChartData);
    });

    // **FIXED ORDERS LISTENER - ALL-TIME ORDER STATUS & ALL-TIME UNPAID REVENUE**
    const unsubscribeOrders = onSnapshot(invoicesRef, (snapshot) => {
      console.log("\n📋 ===== ORDERS LISTENER TRIGGERED =====");
      console.log("📋 Orders snapshot size:", snapshot.size);

      // **TODAY'S DATA ONLY**
      const todayOrdersCount = {};
      const todayRestaurantOrders = new Map();
      const todayInvoicesData = [];

      // **ALL-TIME DATA**
      const allTimeStatusCount = new Map();
      const allRestaurantUnpaid = new Map();
      const salesData = [];

      if (!snapshot.empty) {
        snapshot.forEach((doc, index) => {
          const invoice = { id: doc.id, ...doc.data() };
          console.log(`📋 Invoice ${index + 1}:`, {
            id: invoice.id,
            restaurantName: invoice.restaurantName,
            orderStatus: invoice.orderStatus,
            isBillPaid: invoice.isBillPaid,
            createdAt: invoice.createdAt,
            itemsCount: invoice.items?.length || 0
          });

          const createdAt = invoice.createdAt;
          let invoiceDate;

          if (createdAt && createdAt.seconds) {
            invoiceDate = new Date(createdAt.seconds * 1000);
          } else if (typeof createdAt === "string") {
            invoiceDate = new Date(createdAt);
          }

          let isTodayInvoice = false;
          if (invoiceDate) {
            // Convert invoice date to UK time for comparison
            const invoiceUKTime = new Date(invoiceDate.toLocaleString("en-US", { timeZone: "Europe/London" }));
            isTodayInvoice = invoiceUKTime >= todayStart && invoiceUKTime < todayEnd;

            console.log(`📅 Invoice ${invoice.id} UK time check:`, {
              original: invoiceDate.toString(),
              ukTime: invoiceUKTime.toString(),
              isToday: isTodayInvoice
            });
          }
          const isPerformanceInvoice = invoiceDate && invoiceDate >= performanceStart && invoiceDate <= performanceEnd;

          console.log(`📅 Invoice ${invoice.id} dates:`, {
            date: invoiceDate?.toISOString(),
            isToday: isTodayInvoice,
            isInPerformanceRange: isPerformanceInvoice
          });

          // **🔹 TODAY'S INVOICES ONLY** (for "Today's Orders by Restaurant")
          if (isTodayInvoice) {
            console.log(`✅ TODAY'S Invoice: ${invoice.id}`);

            if (invoice.restaurantName) {
              todayRestaurantOrders.set(
                invoice.restaurantName,
                (todayRestaurantOrders.get(invoice.restaurantName) || 0) + 1
              );
              todayInvoicesData.push(invoice);
              todayOrdersCount[invoice.restaurantName] = (todayOrdersCount[invoice.restaurantName] || 0) + 1;
            }
          }

          // **🔹 ALL-TIME ORDER STATUS** (for Order Status pie chart)
          if (invoice.orderStatus) {
            const status = (invoice.orderStatus || "Pending").charAt(0).toUpperCase() +
              (invoice.orderStatus || "Pending").slice(1);
            allTimeStatusCount.set(status, (allTimeStatusCount.get(status) || 0) + 1);
            console.log(`📊 ALL-TIME Order Status: ${status}, Count: ${allTimeStatusCount.get(status)}`);
          }

          // **🔹 ALL UNPAID REVENUE** (for Pending Revenue by Restaurant)
          if (!invoice.isBillPaid && invoice.restaurantName && invoice.items) {
            const invoiceTotal = invoice.items.reduce(
              (sum, item) => sum + (item.price * item.quantity || 0), 0
            );
            allRestaurantUnpaid.set(
              invoice.restaurantName,
              (allRestaurantUnpaid.get(invoice.restaurantName) || 0) + invoiceTotal
            );
            console.log(`💰 ALL-TIME Unpaid: ${invoice.restaurantName} = £${allRestaurantUnpaid.get(invoice.restaurantName).toFixed(2)}`);
          }

          // **🔹 SALES TREND (Last 7 days for performance)**
          if (isPerformanceInvoice) {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            if (invoiceDate && invoiceDate >= sevenDaysAgo) {
              const formattedDate = invoiceDate.toLocaleDateString("en-GB", {
                day: "2-digit", month: "short"
              });
              const existingDay = salesData.find(item => item.date === formattedDate);
              const dailySales = invoice.items.reduce(
                (sum, item) => sum + item.price * item.quantity, 0
              );

              console.log(`📈 Sales data for ${formattedDate}: £${dailySales.toFixed(2)}`);

              if (existingDay) {
                existingDay.Sales += dailySales;
              } else {
                salesData.push({ date: formattedDate, Sales: dailySales });
              }
            }
          }
        });

        // **PROCESS FINAL DATA**

        // Sort sales data
        salesData.sort((a, b) => {
          const [dayA, monthA] = a.date.split(" ");
          const [dayB, monthB] = b.date.split(" ");
          const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          const monthIndexA = months.indexOf(monthA);
          const monthIndexB = months.indexOf(monthB);
          return monthIndexA * 31 + parseInt(dayA) - (monthIndexB * 31 + parseInt(dayB));
        });

        // **🔹 ALL-TIME ORDER STATUS ARRAY**
        const orderStatusArray = Array.from(allTimeStatusCount, ([name, value]) => ({ name, value }));

        // **🔹 TODAY'S RESTAURANT ORDERS ARRAY**
        const todayRestaurantOrdersArray = Array.from(todayRestaurantOrders, ([name, orders]) => ({
          name: name.length > 15 ? name.substring(0, 15) + '...' : name,
          fullName: name,
          orders
        }));

        // **🔹 ALL UNPAID REVENUE ARRAY**
        const restaurantUnpaidArray = Array.from(allRestaurantUnpaid, ([name, unpaid]) => ({
          name: name.length > 15 ? name.substring(0, 15) + '...' : name,
          fullName: name,
          unpaid: parseFloat(unpaid.toFixed(2))
        }));

        console.log("📊 FINAL DATA SUMMARY:");
        console.log("📊 Sales Trend (7 days):", salesData.length, "entries");
        console.log("📊 ALL-TIME Order Status:", orderStatusArray);
        console.log("📊 TODAY'S Restaurant Orders:", todayRestaurantOrdersArray.length, "restaurants");
        console.log("📊 ALL UNPAID Revenue:", restaurantUnpaidArray.length, "restaurants with unpaid bills");
        console.log("📊 TODAY'S Invoices Count:", todayInvoicesData.length);

        // **SET ALL STATE**
        setOrderStatusData(orderStatusArray); // ✅ NOW ALL-TIME DATA
        setTodayOrders(todayOrdersCount); // ✅ TODAY'S DATA
        setSalesTrend(salesData); // ✅ PERFORMANCE DATA
        setTodayRestaurantOrders(todayRestaurantOrdersArray); // ✅ TODAY'S DATA
        setTodayInvoices(todayInvoicesData); // ✅ TODAY'S DATA
        setRestaurantUnpaidRevenue(restaurantUnpaidArray); // ✅ NOW ALL-TIME UNPAID DATA
      } else {
        console.log("⚠️ No orders found in snapshot");
      }
    });

    // **PURCHASE ORDERS LISTENER**
    const unsubscribePurchaseOrders = onSnapshot(purchaseOrdersRef, async (snapshot) => {
      console.log("\n🛒 ===== PURCHASE ORDERS LISTENER TRIGGERED =====");
      console.log("🛒 Purchase orders snapshot size:", snapshot.size);

      const pendingOrders = [];

      try {
        const ingredientsSnapshot = await getDocs(ingredientsRef);
        const ingredientsData = ingredientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log("🥕 Ingredients data for purchase orders:", ingredientsData.length);

        snapshot.forEach((doc, index) => {
          const order = doc.data();
          console.log(`🛒 Purchase Order ${index + 1}:`, {
            id: doc.id,
            status: order.status,
            orderDate: order.orderDate,
            orderItemsCount: order.orderItems?.length || 0
          });

          if (order.status === "pending") {
            const orderItemsWithDetails = order.orderItems.map(item => {
              const ingredient = ingredientsData.find(ing => ing.id === item.id);
              console.log(`🛒 Order item:`, {
                itemId: item.id,
                foundIngredient: !!ingredient,
                title: ingredient?.title || item.title
              });
              return {
                ...item,
                title: ingredient?.title || item.title,
                units: ingredient?.units || item.units
              };
            });

            const pendingOrder = {
              id: doc.id,
              date: order.orderDate ? new Date(order.orderDate).toLocaleDateString() : "N/A",
              items: orderItemsWithDetails
            };

            pendingOrders.push(pendingOrder);
            console.log(`🛒 Added pending order:`, pendingOrder);
          }
        });

        console.log("🛒 Final pending orders:", pendingOrders.length);
        setPendingPurchaseOrders(pendingOrders);
      } catch (error) {
        console.error("❌ Error processing purchase orders:", error);
      }
    });

    // **TOTAL SOLD LISTENER**
    const unsubscribeTotalSold = onSnapshot(invoicesRef, (snapshot) => {
      console.log("\n💰 ===== TOTAL SOLD LISTENER TRIGGERED =====");
      console.log("💰 Processing all invoices for sold totals:", snapshot.size);

      let totalSoldItems = 0;
      let totalSoldCost = 0;
      let totalSoldQuantity = 0;
      let totalPaid = 0;
      let totalUnpaid = 0;

      snapshot.forEach((doc, index) => {
        const invoice = doc.data();
        let invoiceTotalCost = 0;

        console.log(`💰 Invoice ${index + 1} (${doc.id}):`, {
          restaurantName: invoice.restaurantName,
          isBillPaid: invoice.isBillPaid,
          itemsCount: invoice.items?.length || 0
        });

        invoice.items.forEach((item, itemIndex) => {
          totalSoldItems += 1;
          const itemCost = parseFloat(item.price) * safeNumber(item.quantity) || 0;
          invoiceTotalCost += itemCost;
          totalSoldCost += itemCost;
          totalSoldQuantity += safeNumber(item.quantity) || 0;

          console.log(`💰 Item ${itemIndex + 1}:`, {
            title: item.title,
            price: item.price,
            quantity: item.quantity,
            cost: itemCost
          });
        });

        if (invoice.isBillPaid) {
          totalPaid += invoiceTotalCost;
          console.log(`✅ Paid invoice total: £${invoiceTotalCost.toFixed(2)}`);
        } else {
          totalUnpaid += invoiceTotalCost;
          console.log(`❌ Unpaid invoice total: £${invoiceTotalCost.toFixed(2)}`);
        }
      });

      const soldState = {
        items: totalSoldItems,
        cost: totalSoldCost.toFixed(2),
        quantity: totalSoldQuantity,
        totalPaid: totalPaid.toFixed(2),
        totalUnpaid: totalUnpaid.toFixed(2),
      };

      console.log("💰 Final sold totals:", soldState);
      setTotalSold(soldState);
    });

    // **GET RESTAURANT PERFORMANCE DATA**
    const getResturantPerformanceData = async () => {
      console.log("\n🏪 ===== FETCHING RESTAURANT PERFORMANCE =====");
      try {
        const restaurantPerformanceData = await fetchRestaurantPerformance(db);
        console.log("🏪 Restaurant performance data:", restaurantPerformanceData);
        setRestaurantPerformance(restaurantPerformanceData);
      } catch (error) {
        console.error("❌ Error fetching restaurant performance:", error);
      }
    };

    getResturantPerformanceData();

    console.log("🚀 All listeners set up successfully");

    return () => {
      console.log("🛑 Cleaning up all listeners");
      unsubscribeOrders();
      unsubscribeTotalAvailable();
      unsubscribeTotalSold();
      unsubscribePurchaseOrders();
    };
  }, []);


  // **COMBINED METRICS EFFECT**
  useEffect(() => {
    const combinedItems = safeNumber(totalAvailable.items) + safeNumber(totalSold.items);
    const combinedCost = (parseFloat(totalAvailable.cost) || 0) + (parseFloat(totalSold.cost) || 0);
    const combinedQuantity = safeNumber(totalAvailable.quantity) + safeNumber(totalSold.quantity);

    setTotalCombined({
      items: combinedItems,
      cost: combinedCost.toFixed(2),
      quantity: combinedQuantity,
    });
  }, [totalAvailable, totalSold]);

  // **ENHANCED COMPONENTS**
  const TodayOrdersTable = ({ data }) => (
    <TableContainer component={Paper} sx={{
      borderRadius: activeTheme.borderRadius.medium,
      boxShadow: activeTheme.shadows.whisper,
      border: `1px solid ${activeTheme.colors.borderPrimary}`
    }}>
      <Table size="small">
        <TableHead sx={{
          backgroundColor: alpha(activeTheme.colors.primary, 0.1)
        }}>
          <TableRow>
            <TableCell sx={{ fontWeight: activeTheme.typography.fontWeights.bold, color: activeTheme.colors.primary }}>
              Restaurant
            </TableCell>
            <TableCell sx={{ fontWeight: activeTheme.typography.fontWeights.bold, color: activeTheme.colors.primary }} align="center">
              Orders
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              key={index}
              sx={{
                '&:nth-of-type(even)': { backgroundColor: activeTheme.colors.surfaceElevated },
                '&:hover': { backgroundColor: alpha(activeTheme.colors.primary, 0.05) }
              }}
            >
              <TableCell>
                <Tooltip title={row.fullName || row.name}>
                  <Typography variant="body2" sx={{ color: activeTheme.colors.textPrimary, fontWeight: 500 }}>
                    {row.fullName}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={row.orders}
                  size="small"
                  sx={{
                    backgroundColor: activeTheme.colors.primary,
                    color: activeTheme.colors.surface,
                    fontWeight: 600,
                    borderRadius: '8px'
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // **LOW STOCK ITEMS COMPONENT** (keeping your existing component)
  const LowStockItemsGrid = ({ items }) => {
    const displayItems = showAllLowStock ? items : items.slice(0, ITEMS_PER_PAGE);
    const startIndex = (lowStockPage - 1) * ITEMS_PER_PAGE;
    const paginatedItems = showAllLowStock ? displayItems : items.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const getSeverityColor = (severity) => {
      switch (severity) {
        case 'critical': return activeTheme.colors.error;
        case 'high': return activeTheme.colors.warning;
        case 'medium': return activeTheme.colors.secondary;
        default: return activeTheme.colors.textMuted;
      }
    };

    if (items.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4, color: activeTheme.colors.success }}>
          <CheckCircle size={48} style={{ marginBottom: 16 }} />
          <Typography variant="h6" sx={{ color: activeTheme.colors.success }}>
            🎉 All Items Well Stocked!
          </Typography>
          <Typography variant="body2" sx={{ color: activeTheme.colors.textSecondary, mt: 1 }}>
            No items are currently below their low stock thresholds
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" sx={{ color: activeTheme.colors.textSecondary }}>
            Showing {paginatedItems.length} of {items.length} low stock items
          </Typography>

          {items.length > ITEMS_PER_PAGE && (
            <Tooltip title={showAllLowStock ? "Show paginated view" : "Show all items"}>
              <IconButton
                onClick={() => setShowAllLowStock(!showAllLowStock)}
                size="small"
                sx={{ color: activeTheme.colors.primary }}
              >
                {showAllLowStock ? <EyeOff size={18} /> : <Eye size={18} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Grid container spacing={2}>
          {paginatedItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={item.id || index}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                padding: activeTheme.spacing.md,
                borderRadius: activeTheme.borderRadius.medium,
                border: `1px solid ${activeTheme.colors.borderSubtle}`,
                borderLeft: `5px solid ${getSeverityColor(item.severity)}`,
                backgroundColor: activeTheme.colors.surface,
                transition: activeTheme.transitions.smooth,
                boxShadow: activeTheme.shadows.whisper,
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: activeTheme.shadows.soft,
                  transform: 'translateY(-2px)',
                }
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{
                    fontWeight: activeTheme.typography.fontWeights.bold,
                    color: activeTheme.colors.textPrimary,
                    flex: 1,
                    lineHeight: 1.2,
                    pr: 1
                  }}>
                    {item.title}
                  </Typography>

                  <Chip
                    label={item.severity}
                    size="small"
                    sx={{
                      backgroundColor: alpha(getSeverityColor(item.severity), 0.1),
                      color: getSeverityColor(item.severity),
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                      height: '20px'
                    }}
                  />
                </Box>

                <Box sx={{ mb: 'auto', display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="h5" sx={{
                    fontWeight: 800,
                    color: getSeverityColor(item.severity),
                    letterSpacing: '-0.5px'
                  }}>
                    {item.quantity}
                  </Typography>
                  <Typography variant="body1" sx={{
                    fontWeight: 600,
                    color: activeTheme.colors.textSecondary,
                  }}>
                    / {item.threshold} <span style={{ fontSize: '0.85em', color: activeTheme.colors.textMuted }}>{item.units}</span>
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Chip
                    label={item.type}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: activeTheme.colors.borderPrimary,
                      color: activeTheme.colors.textSecondary,
                      fontSize: '0.75rem',
                      height: '24px'
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {!showAllLowStock && items.length > ITEMS_PER_PAGE && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={Math.ceil(items.length / ITEMS_PER_PAGE)}
              page={lowStockPage}
              onChange={(event, value) => setLowStockPage(value)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <DashboardContainer sx={{ backgroundColor: activeTheme.colors.surfaceLuxury }}>
      {/* **HEADER** - Show for all roles */}
      <HeaderCard sx={{ 
        background: activeTheme.gradients.hero, 
        color: activeTheme.colors.onPrimary,
        padding: activeTheme.spacing.xl, 
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h3" sx={{
              fontWeight: activeTheme.typography.fontWeights.black,
              marginBottom: activeTheme.spacing.sm,
              color: activeTheme.colors.onPrimary
            }}>
              {isChef ? "👨‍🍳 Chef Dashboard" : "📊 Business Intelligence Dashboard"}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              {isChef ? "Kitchen management and order overview" : "Real-time insights and performance analytics"}
            </Typography>
          </Box>

          {/* **MOBILE ORDERS SWITCH - Only show for admin/superadmin** */}
          {showFullDashboard || isChef && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Mobile Orders
              </Typography>
              <Switch
                checked={isCutoffEnabled}
                onChange={handleToggleChange}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: appTheme.colors.champagne,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: appTheme.colors.champagne,
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </HeaderCard>

      {/* **SALES CARDS - Only show for admin/superadmin** */}
      {showFullDashboard && (
        <Box sx={{ mb: 3 }}>
          <SalesCards />
        </Box>
      )}

      {/* **KPI CARDS - Only show for admin/superadmin** */}
      {showFullDashboard && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            {
              title: "Total Revenue",
              value: `£${totalSold.cost || 0}`,
              icon: <PoundSterling />,
              color: appTheme.colors.primary,
              change: "+12.5%",
              trend: "up"
            },
            {
              title: "Paid Revenue",
              value: `£${totalSold.totalPaid || 0}`,
              icon: <CheckCircle />,
              color: appTheme.colors.secondary,
              change: "+8.3%",
              trend: "up"
            },
            {
              title: "Pending Revenue",
              value: `£${totalSold.totalUnpaid || 0}`,
              icon: <AlertCircle />,
              color: appTheme.colors.accent,
              change: "-2.1%",
              trend: "down"
            }
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <KPICard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{
                        fontWeight: appTheme.typography.fontWeights.bold,
                        color: stat.color,
                        mb: 1
                      }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" sx={{
                        color: appTheme.colors.textSecondary,
                        fontWeight: 500
                      }}>
                        {stat.title}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                      <Avatar sx={{
                        bgcolor: alpha(stat.color, 0.1),
                        color: stat.color,
                        mb: 1,
                        width: 48,
                        height: 48
                      }}>
                        {stat.icon}
                      </Avatar>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {stat.trend === 'up' ? (
                          <ArrowUpRight size={14} style={{ color: appTheme.colors.success }} />
                        ) : (
                          <ArrowDownRight size={14} style={{ color: appTheme.colors.error }} />
                        )}
                        <Typography variant="caption" sx={{
                          color: stat.trend === 'up' ? appTheme.colors.success : appTheme.colors.error,
                          fontWeight: 600
                        }}>
                          {stat.change}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </KPICard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* **MAIN CHARTS GRID - Only show for admin/superadmin** */}
      {showFullDashboard && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Sales Trend */}
          <Grid item xs={12}>
            <ChartCard sx={{ minHeight: 400 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <TrendingUp style={{ color: appTheme.colors.primary }} />
                  <Typography variant="h6" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.primary,
                  }}>
                    Weekly Sales Performance
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={salesTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: appTheme.colors.textSecondary }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: appTheme.colors.textSecondary }}
                    />
                    <RechartsTooltip
                      content={<CustomTooltip />}
                    />
                    <Area
                      type="monotone"
                      dataKey="Sales"
                      stroke={appTheme.colors.primary}
                      fill={alpha(appTheme.colors.primary, 0.3)}
                      strokeWidth={3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </ChartCard>
          </Grid>

          {/* Category Performance */}
          <Grid item xs={12} md={8}>
            <ChartCard>
              <CardContent>
                <Typography variant="h6" gutterBottom color={appTheme.colors.textSecondary}>
                  Category Performance
                </Typography>
                {categoryData.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: appTheme.colors.textMuted }}>
                    No category data available
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                      data={categoryData}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 14 }}
                      />
                      <YAxis
                        domain={[0, 'dataMax + 5']}
                        tick={{ fontSize: 14 }}
                      />
                      <RechartsTooltip
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const fullItem = categoryData.find(d => d.name === label);
                            return (
                              <Box sx={{
                                backgroundColor: 'white',
                                padding: 1,
                                border: '1px solid #ccc',
                                borderRadius: 1
                              }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {fullItem?.fullName || label}
                                </Typography>
                                <Typography variant="body2">
                                  Available: {payload[0].value} units
                                </Typography>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar
                        dataKey="Sales"
                        fill={appTheme.colors.primary}
                        minBarSize={5}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </ChartCard>
          </Grid>

          {/* Inventory Type Distribution */}
          <Grid item xs={12} md={4}>
            <ChartCard sx={{ minHeight: 350 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Layers style={{ color: appTheme.colors.accent }} />
                  <Typography variant="h6" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.accent,
                  }}>
                    Inventory Distribution
                  </Typography>
                </Box>
                {inventoryTypeData.length === 0 ? (
                  <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      No inventory data available
                    </Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={inventoryTypeData}
                        dataKey="count"
                        nameKey="type"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        innerRadius={50}
                        paddingAngle={3}
                      >
                        {inventoryTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value, name) => [
                          `${value} items (${inventoryTypeData.find(d => d.type === name)?.percentage || 0}%)`,
                          name
                        ]}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </ChartCard>
          </Grid>

          {/* Restaurant Performance */}
          <Grid item xs={12} md={8}>
            <ChartCard>
              <CardContent>
                <Typography variant="h6" gutterBottom color={appTheme.colors.textSecondary}>
                  Restaurant Performance
                </Typography>
                {restaurantPerformance.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: appTheme.colors.textMuted }}>
                    No restaurant performance data
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                      data={restaurantPerformance}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 14 }}
                      />
                      <YAxis
                        domain={[0, 'dataMax + 10']}
                        tick={{ fontSize: 14 }}
                        tickFormatter={(value) => `£${value}`}
                      />
                      <RechartsTooltip
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            return (
                              <Box sx={{
                                backgroundColor: 'white',
                                padding: 1,
                                border: '1px solid #ccc',
                                borderRadius: 1
                              }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {label}
                                </Typography>
                                <Typography variant="body2">
                                  Revenue: £{Number(payload[0].value).toFixed(2)}
                                </Typography>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar
                        dataKey="revenue"
                        fill={appTheme.colors.secondary}
                        minBarSize={5}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </ChartCard>
          </Grid>

          {/* Order Status Distribution */}
          <Grid item xs={12} lg={4}>
            <ChartCard sx={{ minHeight: 350 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Package style={{ color: appTheme.colors.secondary }} />
                  <Typography variant="h6" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.secondary,
                  }}>
                    Order Status
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={orderStatusData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={60}
                      paddingAngle={2}
                    >
                      {orderStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltip currency={false} />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </ChartCard>
          </Grid>

          {/* Today's Orders */}
           <Grid item xs={12} md={6}>
            <ChartCard sx={{ minHeight: 350 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Users style={{ color: appTheme.colors.warmCopper }} />
                  <Typography variant="h6" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.warmCopper,
                  }}>
                    Today's Orders by Restaurant
                  </Typography>
                </Box>
                <TodayOrdersTable data={todayRestaurantOrders} />
              </CardContent>
            </ChartCard>
          </Grid>

          {/* Pending Revenue */}
          <Grid item xs={12} md={6}>
            <ChartCard sx={{ minHeight: 350 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <AlertCircle style={{ color: appTheme.colors.warning }} />
                  <Typography variant="h6" sx={{
                    fontWeight: appTheme.typography.fontWeights.bold,
                    color: appTheme.colors.warning,
                  }}>
                    Pending Revenue by Restaurant
                  </Typography>
                </Box>
                {restaurantUnpaidRevenue.length === 0 ? (
                  <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      No pending revenue data
                    </Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={restaurantUnpaidRevenue}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        textAnchor="end"
                        height={80}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RechartsTooltip
                        formatter={(value, name) => [`£${value}`, "Pending Amount"]}
                        labelFormatter={(label) => restaurantUnpaidRevenue.find(d => d.name === label)?.fullName || label}
                      />
                      <Bar
                        dataKey="unpaid"
                        fill={appTheme.colors.warning}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </ChartCard>
          </Grid>
        </Grid>
      )}

{
  isChef && <Grid item xs={12} md={6}>
            <ChartCard sx={{ 
              minHeight: 350, 
              backgroundColor: activeTheme.colors.surface, 
              border: `1px solid ${activeTheme.colors.borderSubtle}`, 
              boxShadow: activeTheme.shadows.medium 
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Users style={{ color: activeTheme.colors.primary }} />
                  <Typography variant="h6" sx={{
                    fontWeight: activeTheme.typography.fontWeights.bold,
                    color: activeTheme.colors.primary,
                  }}>
                    Today's Orders by Restaurant
                  </Typography>
                </Box>
                <TodayOrdersTable data={todayRestaurantOrders} />
              </CardContent>
            </ChartCard>
          </Grid>
}
      {/* **TODAY'S INVOICES - Show for all roles** */}
      <TodayInvoicesGrid invoices={todayInvoices} />

      {/* **LOW STOCK ALERTS - Show for all roles** */}
      {/* **LOW STOCK ALERTS - Show for all roles** */}
      <StyledCard sx={{ 
        mb: 3, 
        mt: 3,
        backgroundColor: activeTheme.colors.surface, 
        border: `1px solid ${activeTheme.colors.borderSubtle}`, 
        boxShadow: activeTheme.shadows.medium 
      }}>
        <Accordion
          defaultExpanded
          sx={{
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<ChevronDown />}
            sx={{
              backgroundColor: alpha(activeTheme.colors.warning, 0.1),
              borderRadius: activeTheme.borderRadius.medium,
              mb: 2,
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                gap: 2
              }
            }}
          >
            <AlertCircle style={{ color: activeTheme.colors.warning }} />
            <Typography variant="h6" sx={{
              fontWeight: activeTheme.typography.fontWeights.bold,
              color: activeTheme.colors.warning
            }}>
              Inventory Alerts
            </Typography>
            <Badge
              badgeContent={lowStockItems.length}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: activeTheme.colors.error,
                  color: activeTheme.colors.surface
                }
              }}
            >
              <Package size={20} />
            </Badge>
          </AccordionSummary>
          <AccordionDetails>
            <LowStockItemsGrid items={lowStockItems} />
          </AccordionDetails>
        </Accordion>
      </StyledCard>

      {/* **CHEF-SPECIFIC MESSAGE - Only show if chef and no other content** */}
      {showLimitedDashboard && (
        <Box sx={{ textAlign: 'center', py: 4, mt: 4, 
          backgroundColor: activeTheme.colors.surfaceLuxury, 
          borderRadius: 4,
          border: `1px solid ${activeTheme.colors.borderPrimary}`
        }}>
          <Typography variant="h5" sx={{ color: activeTheme.colors.primary, fontWeight: 'bold' }}>
            👨‍🍳 Chef Dashboard
          </Typography>
          <Typography variant="body1" sx={{ color: activeTheme.colors.textSecondary, mt: 1 }}>
            View today's orders and inventory alerts to manage kitchen operations efficiently.
          </Typography>
        </Box>
      )}
    </DashboardContainer>
  );
};

export default Dashboard;
